import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import ServiceService from '../services/serviceService';
import UpcomingServicesTable from './UpcomingServicesTable';
import ResponsiveTable from '../../../components/common/ResponsiveTable';
import { serviceRenewalTableColumns } from '../schemas/serviceRenewalSchemas';

const SchedulerPanel = () => {
  const [upcomingServices, setUpcomingServices] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [renewalsLoading, setRenewalsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [renewingId, setRenewingId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadUpcomingServices();
    loadRenewals();
  }, []);

  const loadUpcomingServices = async () => {
    try {
      setUpcomingLoading(true);
      const response = await ServiceService.getUpcomingServices(30); // 30 days ahead
      if (response.success) {
        setUpcomingServices(response.data.services || []);
        setError(null);
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError('Σφάλμα φόρτωσης επερχόμενων υπηρεσιών');
    } finally {
      setUpcomingLoading(false);
    }
  };

  const handleEarlyRenew = async (service) => {
    setSuccess(null);
    setError(null);
    setRenewingId(service.id);
    try {
      const result = await ServiceService.earlyRenewService(service.id);
      if (result.success) {
        setSuccess(
          `Δημιουργήθηκε η επόμενη περίοδος (υπηρεσία #${result.data?.new_service_id}). Μπορείτε να εκδώσετε τιμολόγιο.`
        );
        await loadUpcomingServices();
        loadRenewals();
      } else {
        setError(result.error);
      }
    } catch (e) {
      setError('Σφάλμα πρόωρης ανανέωσης');
    } finally {
      setRenewingId(null);
    }
  };

  const loadRenewals = async () => {
    try {
      setRenewalsLoading(true);
      const result = await ServiceService.getRenewals();
      if (result.success) {
        setRenewals(result.data || []);
        setError(null);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Σφάλμα φόρτωσης ανανεώσεων');
    } finally {
      setRenewalsLoading(false);
    }
  };

  const handleProcessRenewal = async (renewal) => {
    try {
      const result = await ServiceService.processRenewal(renewal.id);
      if (result.success) {
        loadRenewals();
        return { success: true };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      setError('Σφάλμα επεξεργασίας ανανέωσης');
      return { success: false, error: 'Σφάλμα επεξεργασίας ανανέωσης' };
    }
  };


  // Enhanced columns for renewals table
  const enhancedRenewalColumns = [
    ...serviceRenewalTableColumns,
    {
      field: 'actions',
      headerName: 'Ενέργειες',
      sortable: false,
      minWidth: 120,
      renderCell: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {row.status === 'pending' && (
            <Tooltip title="Επεξεργασία">
              <IconButton
                size="small"
                onClick={() => handleProcessRenewal(row)}
                sx={{ color: 'primary.main' }}
              >
                <ScheduleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ minHeight: 36 }}>
          <Tab label={<Typography variant="caption">Επερχόμενες Χρεώσεις</Typography>} />
          <Tab label={<Typography variant="caption">Ανανεώσεις Υπηρεσιών</Typography>} />
        </Tabs>
        <Tooltip title="Ανανέωση">
          <IconButton
            size="small"
            onClick={() => {
              loadUpcomingServices();
              loadRenewals();
            }}
            disabled={upcomingLoading || renewalsLoading}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <UpcomingServicesTable
          services={upcomingServices}
          loading={upcomingLoading}
          error={null}
          onEarlyRenew={handleEarlyRenew}
          renewingId={renewingId}
        />
      )}

      {activeTab === 1 && (
        <Box>
          <ResponsiveTable
            data={renewals}
            columns={enhancedRenewalColumns}
            loading={renewalsLoading}
            emptyMessage="Δεν βρέθηκαν ανανεώσεις"
            sortField="renewal_date"
            sortDirection="desc"
          />
        </Box>
      )}
    </Box>
  );
};

export default SchedulerPanel;
